import { Box, Divider, Flex, Menu, Stack, rem, useMantineTheme } from '@mantine/core';
import { IconChevronRight, IconCookie, IconDiscountCheck, IconPower, IconUser } from '@tabler/icons-react';
import Avatar from 'components/Avatar';
import Button from 'components/Button';
import H2Medium from 'components/typography/H2Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import P4Regular from 'components/typography/P4Regular';
import NavbarItem from 'layouts/dashboard/navbar/NavbarItem';
import { LOGOUT_PAGE_PATH, MY_CERTIFICATES_PAGE_PATH, MY_PROFILE_PAGE_PATH } from 'routes/paths';
import { useApi } from 'api/api-context';
import { useState } from 'react';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';

/**
 * Display the person's name and avatar.
 */
export default function NavbarPerson({ isCollapsed }: { isCollapsed: boolean }) {
  const { fullName, email, role, roleId, organizationName, hasPermissionAnywhere } = useApi();
  const { isMobile } = useResponsiveDesign();
  const { revisions } = useOrganizationModules();
  const theme = useMantineTheme();
  const [hover, setHover] = useState(false);
  const isCompact = isCollapsed && isMobile;
  const avatarSize = isCompact ? 'm' : 'l';
  const avatarDimension = isCompact ? 24 : 40;

  return (
    <Menu position="right-end" withinPortal>
      <Menu.Target>
        <Flex
          onMouseEnter={() => {
            setHover(true);
          }}
          onMouseLeave={() => {
            setHover(false);
          }}
          sx={(theme) => ({
            cursor: 'pointer',
            borderRadius: rem(4),
            '&:hover': {
              backgroundColor: theme.colors.gray[0],
            },
          })}
          direction="row"
          justify="start"
          align="center"
          p={isCompact ? 8 : 12}
          gap={isCompact ? 8 : 12}
        >
          <Box w={avatarDimension} h={avatarDimension}>
            <Avatar
              fullName={fullName!}
              size={avatarSize}
              bgColor={hover ? theme.colors.gray[8] : theme.colors.gray[7]}
            />
          </Box>
          {!isCollapsed && (
            <>
              <Box>
                <P1Medium color={hover ? 'gray.8' : 'gray.7'}>{fullName}</P1Medium>
                <P4Regular color={hover ? 'gray.7' : 'gray.6'}>{organizationName}</P4Regular>
              </Box>
              <IconChevronRight size={16} color={hover ? theme.colors.gray[8] : theme.colors.gray[7]} />
            </>
          )}
        </Flex>
      </Menu.Target>

      <Menu.Dropdown>
        <Box miw={256} maw={400}>
          <Box p={16}>
            <H2Medium truncate>{fullName}</H2Medium>
            <P2Regular truncate>{role}</P2Regular>
            <P2Regular truncate color="gray.6">
              {email}
            </P2Regular>
          </Box>

          <Divider color="gray.1" />

          <Stack spacing={4} px={4} py={4}>
            <NavbarItem
              to={MY_PROFILE_PAGE_PATH.original}
              label="Môj profil"
              icon={<IconUser stroke="1.5" />}
              isCollapsed={false}
            />
            {revisions && roleId !== ROLE_ADMIN_ID && hasPermissionAnywhere('fill-out-revisions') && (
              <NavbarItem
                to={MY_CERTIFICATES_PAGE_PATH.original}
                label="Moje osvedčenia"
                icon={<IconDiscountCheck stroke="1.5" />}
                isCollapsed={false}
              />
            )}
            {/* TODO do not use hard-coded link, create page when VOP is provided by the client */}
            <NavbarItem
              target="_blank"
              to="/VOP.pdf"
              label={
                <>
                  Všeobecné obchodné
                  <br />
                  podmienky
                </>
              }
              icon={<IconCookie stroke="1.5" />}
              isCollapsed={false}
            />
          </Stack>

          <Divider color="gray.1" />

          <Box p={4}>
            <Button
              component={NavbarItem}
              label="Odhlásiť sa"
              icon={<IconPower stroke="1.5" />}
              to={LOGOUT_PAGE_PATH.original}
              isCollapsed={false}
            />
          </Box>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}
